import { useRef, useEffect, useState } from "react";
import { useChat, type Message } from "@ai-sdk/react";
import { Streamdown } from "streamdown";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface AIChatDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Tool name to friendly label mapping
const toolLabels: Record<string, { label: string; icon: string }> = {
  // Leads & Applications
  getApplications: { label: "Fetching applications", icon: "📝" },
  getOptins: { label: "Fetching optins", icon: "✉️" },
  getDeals: { label: "Analyzing deals data", icon: "💰" },
  getLeads: { label: "Fetching leads/EOC data", icon: "👥" },
  // Content Performance
  getContentMetrics: { label: "Analyzing content performance", icon: "🎬" },
  // Team Performance
  getSetterPerformance: { label: "Getting setter metrics", icon: "📅" },
  getCloserPerformance: { label: "Getting closer metrics", icon: "🎯" },
  // Call data
  getCallLogs: { label: "Retrieving call logs", icon: "📞" },
  getCallStats: { label: "Calculating call statistics", icon: "📉" },
  // Generic database access
  queryDatabase: { label: "Running database query", icon: "🔍" },
  listCollections: { label: "Listing collections", icon: "📋" },
  getCollectionSchema: { label: "Inspecting schema", icon: "🔧" },
  aggregateData: { label: "Aggregating data", icon: "⚙️" },
};

// Tool invocation indicator
function ToolIndicator({ toolName, state }: { toolName: string; state: "pending" | "running" | "complete" }) {
  const tool = toolLabels[toolName] || { label: toolName, icon: "🔧" };
  const isComplete = state === "complete";

  return (
    <div className="flex justify-start">
      <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all ${
        isComplete
          ? "bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400"
          : "bg-secondary text-muted-foreground"
      }`}>
        {isComplete ? (
          <svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
        )}
        <span>{tool.icon}</span>
        <span>{tool.label}</span>
      </div>
    </div>
  );
}

// Animated thinking indicator component
function ThinkingIndicator() {
  const [dots, setDots] = useState("");

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex justify-start">
      <div className="bg-secondary rounded-2xl px-4 py-3 max-w-[80%]">
        <div className="flex items-center gap-3">
          <div className="relative">
            <svg
              className="w-5 h-5 text-[var(--brand-primary)] animate-pulse"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
              />
            </svg>
            <div className="absolute inset-0 animate-ping">
              <svg
                className="w-5 h-5 text-[var(--brand-primary)] opacity-30"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
                />
              </svg>
            </div>
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium text-foreground">
              Thinking{dots}
            </span>
            <span className="text-xs text-muted-foreground">
              Analyzing your request
            </span>
          </div>
        </div>
        <div className="mt-3 h-1 bg-secondary-foreground/10 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] rounded-full animate-progress" />
        </div>
      </div>
    </div>
  );
}

// Message bubble component
function MessageBubble({ message, isStreaming }: { message: Message; isStreaming?: boolean }) {
  const isUser = message.role === "user";

  // Check for tool invocations in the message
  const toolInvocations = message.toolInvocations || [];

  // If this is an assistant message with tool invocations, render them
  if (!isUser && toolInvocations.length > 0) {
    return (
      <div className="space-y-2">
        {/* Show tool indicators */}
        {toolInvocations.map((tool, idx) => (
          <ToolIndicator
            key={`${tool.toolName}-${idx}`}
            toolName={tool.toolName}
            state={tool.state === "result" ? "complete" : "running"}
          />
        ))}

        {/* Show text content if any */}
        {message.content && (
          <div className="flex justify-start">
            <div className="max-w-[85%] rounded-2xl px-4 py-3 bg-secondary">
              <div className="text-sm prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-0.5">
                <Streamdown>{message.content}</Streamdown>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Regular message (user or assistant without tools)
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-3 ${
          isUser
            ? "bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] text-white"
            : "bg-secondary"
        }`}
      >
        {isUser ? (
          <div className="text-sm whitespace-pre-wrap">{message.content}</div>
        ) : (
          <div className="text-sm prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-0.5">
            {message.content ? (
              <Streamdown>{message.content}</Streamdown>
            ) : isStreaming ? (
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <span className="w-1.5 h-1.5 bg-current rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 bg-current rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 bg-current rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </span>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

export function AIChatDialog({ open, onOpenChange }: AIChatDialogProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { messages, input, handleInputChange, handleSubmit, isLoading, error } = useChat({
    api: "/api/ai/chat",
    streamProtocol: "data",
  });

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // Focus input when dialog opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Suggested prompts for new users
  const suggestedPrompts = [
    "What's our total cash collected this month?",
    "Show me closer performance rankings",
    "Which content has the highest close rate?",
    "How many applications came in this week?",
  ];

  const handleSuggestedPrompt = (prompt: string) => {
    handleInputChange({ target: { value: prompt } } as React.ChangeEvent<HTMLInputElement>);
    setTimeout(() => {
      const submitEvent = new Event("submit", { bubbles: true, cancelable: true });
      handleSubmit(submitEvent as unknown as React.FormEvent<HTMLFormElement>);
    }, 50);
  };

  // Check if we should show the thinking indicator
  const lastMessage = messages[messages.length - 1];
  const showThinking = isLoading && lastMessage?.role === "user";
  const isStreamingResponse = isLoading && lastMessage?.role === "assistant" && !lastMessage.toolInvocations?.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] h-[600px] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <svg className="w-5 h-5 text-[var(--brand-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
            </svg>
            Ask AI Assistant
          </DialogTitle>
        </DialogHeader>

        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {messages.length === 0 && !isLoading ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[var(--brand-gradient-start)]/10 to-[var(--brand-gradient-end)]/10 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-[var(--brand-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">How can I help you?</h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                Ask me about your analytics, ad performance, call statistics, or any data in your dashboard.
              </p>
              <div className="grid grid-cols-2 gap-2 w-full max-w-md">
                {suggestedPrompts.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => handleSuggestedPrompt(prompt)}
                    className="text-left text-sm px-3 py-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((message, idx) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  isStreaming={isStreamingResponse && idx === messages.length - 1}
                />
              ))}

              {/* Show thinking indicator when loading and last message is from user */}
              {showThinking && <ThinkingIndicator />}

              {/* Show error if any */}
              {error && (
                <div className="flex justify-start">
                  <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-2xl px-4 py-3 max-w-[80%]">
                    <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm font-medium">Error</span>
                    </div>
                    <p className="text-sm text-red-500 dark:text-red-400 mt-1">{error.message}</p>
                  </div>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <form onSubmit={handleSubmit} className="px-6 py-4 border-t shrink-0 bg-background">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              name="message"
              value={input}
              onChange={handleInputChange}
              placeholder="Ask about your data..."
              className="flex-1 px-4 py-2.5 rounded-xl border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-offset-2"
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={!input.trim() || isLoading}
              className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] text-white font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            AI can make mistakes. Verify important information.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
